
import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Users, UserCog, UserRound, Linkedin } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { useQuery } from "@tanstack/react-query";
import { OrganizationalStructure } from "@/components/team/OrganizationalStructure";
import { Button } from "@/components/ui/button";

interface TeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  experience: string;
  avatar_url: string | null;
  responsibilities: string[];
  created_at: string;
  linkedin: string | null;
}

const Team = () => {
  const { toast } = useToast();

  const {
    data: teamMembers,
    isLoading: isLoadingTeam,
    error: teamError,
  } = useQuery({
    queryKey: ["team-members"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("team_members")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) {
        console.error("Error fetching team members:", error);
        toast({
          title: "Error",
          description: "Failed to load team members",
          variant: "destructive",
        });
        throw error;
      }

      const sortedData = data
        .filter(member => !member.name.toLowerCase().includes("wissam"))  // Filter out Wissam
        .sort((a, b) => {
          if (a.name.includes("Ahmed")) return -1;
          if (b.name.includes("Ahmed")) return 1;
          if (a.name.includes("Mamduh")) return -1;
          if (b.name.includes("Mamduh")) return 1;
          return 0;
        });

      return sortedData as TeamMember[];
    },
  });

  const getCompanyLogos = (name: string) => {
    if (name.includes("Ahmed")) {
      return [
        "/Partners/microsoft-partner.png",
        "/Partners/Oracle_logo.png",
        "/Partners/KAUST-logof.png",
        "/Partners/AT&T.png",
        "/Partners/Marriott_Logo.png",
      ];
    }
    if (name.includes("Mamduh")) {
      return [
        "/Partners/microsoft-partner.png",
        "/Partners/AT&T.png",
        "/Partners/Oracle_logo.png",
      ];
    }
    return [
      "/Partners/Roshn.png",
      "/Partners/Dallah.png",
      "/Partners/durak.png",
      "/Partners/logo.png",
    ];
  };

  if (isLoadingTeam) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (teamError) {
    return (
      <div className="flex items-center justify-center py-20 text-red-500">
        Failed to load data
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Leadership Team Section */}
      <Card className="bg-white shadow-md">
        <CardHeader className="flex flex-row items-center justify-between border-b pb-6">
          <CardTitle className="text-2xl text-primary flex items-center gap-2">
            <Users className="h-6 w-6" />
            Leadership Team
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-8">
          <div className="grid grid-cols-1 gap-12 max-w-6xl mx-auto px-4">
            {teamMembers?.map((member) => (
              <Card
                key={member.id}
                className="bg-accent/30 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1 w-full"
              >
                <CardContent className="pt-8 pb-6 w-full">
                  <div className="flex flex-col items-center text-center space-y-4">
                    <div className="relative">
                      <Avatar className="h-40 w-40 ring-4 ring-primary/20">
                        {member.avatar_url ? (
                          <AvatarImage
                            src={member.avatar_url}
                            alt={member.name}
                            className="object-cover"
                          />
                        ) : (
                          <AvatarFallback className="bg-primary/10">
                            <UserRound className="h-20 w-20 text-primary" />
                          </AvatarFallback>
                        )}
                      </Avatar>
                    </div>
                    <div className="space-y-3 pt-6">
                      <div className="flex items-center justify-center gap-2">
                        <h3 className="font-semibold text-2xl text-gray-900">
                          {member.name}
                        </h3>
                        {member.linkedin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-blue-600 hover:text-blue-700"
                            asChild
                          >
                            <a
                              href={member.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Linkedin className="h-5 w-5" />
                            </a>
                          </Button>
                        )}
                      </div>
                      <p className="text-primary font-medium text-lg">
                        {member.role}
                      </p>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {member.bio}
                      </p>
                    </div>
                  </div>
                  <div className="border-t mt-6 pt-6">
                    <h4 className="font-medium mb-4 flex items-center gap-2 text-gray-900">
                      <UserCog className="h-5 w-5 text-primary" />
                      Key Responsibilities
                    </h4>
                    <ul className="text-sm space-y-2">
                      {member.responsibilities.map((resp, idx) => (
                        <li
                          key={idx}
                          className="text-muted-foreground flex items-start"
                        >
                          <span className="text-primary mr-2">•</span>
                          <span>{resp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-6 pt-6 border-t">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 items-center justify-items-center">
                      {getCompanyLogos(member.name).map((logo, idx) => (
                        <div
                          key={idx}
                          className="w-24 h-12 bg-white rounded-lg shadow-sm p-2 flex items-center justify-center"
                        >
                          <img
                            src={logo}
                            alt="Company logo"
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Organizational Structure Section */}
      <OrganizationalStructure />
    </div>
  );
};

export default Team;
